import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type DispatchCandidateDTO } from '../lib/api'
import { Icon } from './icons'
import { useToast } from './ui/Toaster'

/**
 * Dispatch notices, waiting for someone to approve them.
 *
 * Shopee only. Mercado Livre rations seller-initiated post-sale messages to one per order and
 * shuts the window after about a week — measured live — and that single slot is the same channel
 * a real problem gets solved through.
 *
 * A queue and not a switch. Nothing in this product has ever reached a customer without a person
 * deciding, and the place to keep that rule hardest is the one that writes to hundreds at once.
 * The exact text is on screen because approving something you cannot read is not approving.
 */
export function DispatchNotices() {
  const qc = useQueryClient()
  const toast = useToast()
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  /**
   * O erro de cada linha fica NA linha.
   *
   * Antes só existia um toast com "0 enviado(s), 1 falhou(ram)" — que some, não diz de qual
   * pedido nem por quê, e foi exatamente assim que o primeiro envio real falhou sem ninguém
   * entender: a Shopee recusou porque a mensagem saiu pela loja errada.
   */
  const [errors, setErrors] = useState<Record<string, string>>({})

  const { data = [], isLoading } = useQuery({
    queryKey: ['dispatch-notices'],
    queryFn: () => api.dispatchNotices(),
    refetchInterval: 60_000,
  })

  const send = useMutation({
    mutationFn: (orderIds: string[]) => api.sendDispatchNotices(orderIds),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['dispatch-notices'] })
      setErrors((prev) => {
        const next = { ...prev }
        for (const res of r.results) {
          if (res.sent) delete next[res.orderId]
          else next[res.orderId] = res.error ?? 'falhou sem motivo informado'
        }
        return next
      })
      toast(
        r.failed
          ? `${r.sent} aviso(s) enviado(s), ${r.failed} falhou(ram) — o motivo está em cada linha.`
          : `${r.sent} aviso(s) enviado(s).`,
      )
    },
    onError: () => toast('Não foi possível enviar os avisos.'),
  })

  const ready = data.filter((c) => !c.blockedReason && !dismissed.has(c.orderId))
  const blocked = data.filter((c) => c.blockedReason)
  if (isLoading || (!data.length && !isLoading)) return null

  return (
    <div className="disp">
      <div className="disp-h">
        <Icon name="truck" size={15} />
        <b>{ready.length} pedido(s) despachado(s) sem aviso ao comprador</b>
        <span className="disp-sub">Shopee · nada é enviado sem você clicar</span>
        <button
          className="btn pri sm"
          disabled={!ready.length || send.isPending}
          onClick={() => send.mutate(ready.map((c) => c.orderId))}
        >
          {send.isPending ? 'Enviando…' : `Enviar ${ready.length}`}
        </button>
      </div>

      <div className="disp-list">
        {ready.map((c) => (
          <div key={c.orderId} className="disp-item">
            <div className="disp-meta">
              <b>{c.externalOrderId}</b>
              <span>{c.buyerName}</span>
              {c.carrier && <span className="disp-carrier">{c.carrier}</span>}
              <button
                className="linkish"
                title="Tirar da lista desta vez"
                onClick={() => setDismissed((s) => new Set(s).add(c.orderId))}
              >
                pular
              </button>
              <button
                className="btn sec sm"
                disabled={send.isPending}
                onClick={() => send.mutate([c.orderId])}
              >
                Enviar
              </button>
            </div>
            {/* Shown in full: approving a message you cannot read is not approving it. */}
            <div className="disp-text">{c.text}</div>
            {errors[c.orderId] && <div className="disp-err">Não enviou: {errors[c.orderId]}</div>}
          </div>
        ))}

        {blocked.map((c) => (
          <div key={c.orderId} className="disp-item blocked">
            <div className="disp-meta">
              <b>{c.externalOrderId}</b>
              <span>{c.buyerName}</span>
              {/* Kept visible rather than filtered away — hiding it would hide the problem. */}
              <span className="disp-block">não dá para enviar: {c.blockedReason}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
